// `/scout` — the conversation that is not about one account.
//
// The sidebar embed prepends "the signed-in user is looking at {account}" to every
// message. This tab sends no account_id, so Scout is free to answer "what should I
// work on this morning". Each new conversation has its own actor-scoped session. Hide, do not stub.

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AccessDeniedError, fetchBook, fetchMe, readViewAs } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { ScoutConversations } from '../components/ScoutConversations';
import { chatActorEmail } from '../lib/chatThread.ts';

export function ScoutView() {
  const { getToken, email, signOut } = useAuthContext();

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const bookQuery = useQuery({ queryKey: ['book'], queryFn: () => fetchBook(getToken) });
  const viewer = meQuery.data?.viewer;

  if (meQuery.error instanceof AccessDeniedError) {
    return (
      <DeniedGate message={meQuery.error.message} email={email} onSignOut={signOut} />
    );
  }
  if (meQuery.isPending) {
    return <div className="loading">Signing you in…</div>;
  }

  // Hide, do not stub. A bookmark to /scout lands on the board this caller already has.
  if (viewer && !viewer.sees_scout_tab) {
    const destination = viewer.default_view === 'execution' ? '/execution' : '/activity';
    return <Navigate to={destination} replace />;
  }

  return (
    <PageChrome
      freshness={bookQuery.data?.freshness ?? null}
      build={bookQuery.data?.build}
      viewer={viewer}
    >
      <ScoutConversations key={chatActorEmail(email, readViewAs())} actor={chatActorEmail(email, readViewAs())} />
    </PageChrome>
  );
}
